import React, { useState, useEffect, useMemo } from 'react';
import auditService from '../services/auditService';

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [stats, setStats] = useState(null);

  // Load logs on mount
  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = () => {
    const allLogs = auditService.getAllLogs();
    setLogs(allLogs);
    setStats(auditService.getStatistics());
  };

  // Filter logs
  const filteredLogs = useMemo(() => {
    return auditService.getFilteredLogs({
      action: filterAction,
      entityType: filterEntityType,
      searchQuery
    });
  }, [logs, searchQuery, filterAction, filterEntityType]);

  // Get unique entity types
  const entityTypes = useMemo(() => {
    const types = new Set(logs.map(log => log.entityType));
    return Array.from(types).sort();
  }, [logs]);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Get action color
  const getActionColor = (action) => {
    switch (action) {
      case 'create': return '#48bb78'; // Green
      case 'update': return '#4299e1'; // Blue
      case 'delete': return '#f56565'; // Red
      case 'view': return '#718096'; // Gray
      default: return '#718096';
    }
  };

  // Get action icon
  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return '+';
      case 'update': return '✎';
      case 'delete': return '×';
      case 'view': return '👁';
      default: return '•';
    }
  };

  // Export logs
  const handleExport = () => {
    const dataStr = auditService.exportLogs();
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-logs-${new Date().toISOString()}.json`;
    link.click();
  };

  return (
    <div className="clinical-mapper">
      {/* Header Card */}
      <div className="content-card">
        <div className="card-header">
          <div className="card-title">Audit Log</div>
          <div>
            <button onClick={handleExport} className="btn btn-primary">
              Export Logs
            </button>
            <button 
              onClick={() => {
                if (window.confirm('Are you sure you want to clear all audit logs? This action cannot be undone.')) {
                  auditService.clearLogs();
                  loadLogs();
                }
              }} 
              className="btn btn-primary"
              style={{ marginLeft: '8px' }}
            >
              Clear Logs
            </button>
          </div>
        </div>
        <div className="card-content">
          {/* Statistics */}
          {stats && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '16px', 
              marginBottom: '24px' 
            }}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f7fafc', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2d3748' }}>
                  {stats.totalLogs}
                </div>
                <div style={{ fontSize: '12px', color: '#718096' }}>Total Logs</div>
              </div>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f7fafc', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#48bb78' }}>
                  {stats.byAction.create || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#718096' }}>Created</div>
              </div>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f7fafc', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4299e1' }}>
                  {stats.byAction.update || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#718096' }}>Updated</div>
              </div>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f7fafc', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f56565' }}>
                  {stats.byAction.delete || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#718096' }}>Deleted</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                minWidth: '200px',
                padding: '10px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              style={{
                padding: '10px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="view">View</option>
            </select>
            <select
              value={filterEntityType}
              onChange={(e) => setFilterEntityType(e.target.value)}
              style={{
                padding: '10px 16px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">All Entity Types</option>
              {entityTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="content-card">
        <div className="card-header">
          <div className="card-title">Activity Log ({filteredLogs.length})</div>
        </div>
        <div className="card-content">
          {filteredLogs.length === 0 ? (
            <div className="empty-state">
              <p>No audit logs found</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredLogs.map(log => (
                <div
                  key={log.id}
                  style={{
                    padding: '16px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}
                >
                  {/* Action Icon */}
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: `${getActionColor(log.action)}20`,
                      color: getActionColor(log.action),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    {getActionIcon(log.action)}
                  </div>

                  {/* Log Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginBottom: '4px',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{ 
                        fontWeight: 600, 
                        color: '#2d3748',
                        textTransform: 'capitalize'
                      }}>
                        {log.action}
                      </span>
                      <span style={{ color: '#718096' }}>•</span>
                      <span style={{ 
                        color: '#4a5568',
                        backgroundColor: '#edf2f7',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {log.entityType}
                      </span>
                      <span style={{ color: '#718096' }}>•</span>
                      <span style={{ color: '#2d3748', fontWeight: 500 }}>
                        {log.entityName}
                      </span>
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#718096',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}>
                      <span>by {log.userName}</span>
                      <span>•</span>
                      <span>{formatTimestamp(log.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
